import {
  Activity,
  BookOpen,
  Bot,
  BrainCircuit,
  type LucideIcon,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  Puzzle,
  Radio,
  Settings,
  ShieldCheck,
  Store,
  Users,
  Wand2,
  Wrench,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Brand } from "@/components/brand";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useHighlightedPathname } from "@/lib/highlighted-pathname";
import { useT } from "@/lib/i18n";
import type { TranslationKey } from "@/lib/i18n-dict";
import { cn } from "@/lib/utils";

interface NavItem {
  href: string;
  /** Translation key — looked up at render time so the rail re-renders on language switch. */
  labelKey: TranslationKey;
  icon: LucideIcon;
  /** Optional active-route predicate; defaults to exact match. */
  match?: (pathname: string) => boolean;
}

/**
 * Primary destinations — sit above the divider and grow as the
 * workspace surfaces ship. Order mirrors the agent operator's day:
 * chat first, agents/skills/knowledge/channels/MCP next (the things
 * an agent is configured with), then activity/LLM/teams (the things
 * you inspect and govern).
 */
const PRIMARY: NavItem[] = [
  { href: "/chat", labelKey: "nav.chat", icon: MessageSquare },
  { href: "/agents", labelKey: "nav.agents", icon: Bot, match: (p) => p.startsWith("/agents") },
  { href: "/skills", labelKey: "nav.skills", icon: Puzzle, match: (p) => p.startsWith("/skills") },
  {
    href: "/marketplace",
    labelKey: "nav.marketplace",
    icon: Store,
    match: (p) => p.startsWith("/marketplace"),
  },
  { href: "/knowledge", labelKey: "nav.knowledge", icon: BookOpen },
  {
    href: "/workshop",
    labelKey: "nav.workshop",
    icon: Wrench,
    match: (p) => p.startsWith("/workshop"),
  },
  { href: "/channels", labelKey: "nav.channels", icon: Radio },
  { href: "/mcp", labelKey: "nav.mcp", icon: Wand2, match: (p) => p.startsWith("/mcp") },
  {
    href: "/approvals",
    labelKey: "nav.approvals",
    icon: ShieldCheck,
    match: (p) => p.startsWith("/approvals"),
  },
  { href: "/evaluations", labelKey: "nav.activity", icon: Activity },
  { href: "/llm", labelKey: "nav.llm", icon: BrainCircuit, match: (p) => p.startsWith("/llm") },
  { href: "/teams", labelKey: "nav.teams", icon: Users },
];

/**
 * Operations / admin links pinned to the bottom — visually subdued
 * because most days you don't touch them.
 */
const SECONDARY: NavItem[] = [
  {
    href: "/settings",
    labelKey: "nav.settings",
    icon: Settings,
    match: (p) => p.startsWith("/settings"),
  },
];

const COLLAPSED_KEY = "knode.sidebar.collapsed";

/**
 * Collapsible workspace sidebar.
 *
 * Has two visual states that flip via the toggle in the header:
 *  - Expanded (~13rem): brand wordmark, label per item, active rail
 *    accent on the left edge.
 *  - Collapsed (~3.5rem): logo + icon-only rail, label promoted to a
 *    Radix tooltip on hover. Mirrors the lemon_agent slim rail.
 *
 * The chosen state is persisted to localStorage so users don't have
 * to re-collapse on every reload. We render the initial markup with
 * a sentinel `data-mounted="false"` flag and only flip to the real
 * preference on mount — that way SSR + hydration stays deterministic
 * and we don't get a flash of the wrong width.
 *
 * The "active" treatment matches lemon_agent's icon rail: a clay
 * tint plus a 3px primary bar on the left edge so the selected
 * route is obvious in either state.
 */
export function AppSidebar() {
  const pathname = useHighlightedPathname();
  // `RailItem` looks up its own labels; we only need `t` here for the
  // collapse/expand affordance and brand wordmark in the header strip.
  const t = useT();
  const [collapsed, setCollapsed] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    try {
      const v = window.localStorage.getItem(COLLAPSED_KEY);
      if (v === "1") setCollapsed(true);
    } catch {
      /* localStorage may be disabled in private mode */
    }
    setMounted(true);
  }, []);

  const toggle = (): void => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  return (
    <aside
      data-mounted={mounted}
      className={cn(
        "flex h-full shrink-0 flex-col border-r border-border/70 bg-[hsl(var(--rail))]",
        "transition-[width] duration-200 ease-out",
        collapsed ? "w-[56px]" : "w-[224px]",
      )}
    >
      {/* Brand + collapse toggle. The toggle straddles the right
       * edge so it stays reachable in both states; in collapsed
       * mode it's the only header content. */}
      <div
        className={cn(
          "flex h-12 shrink-0 items-center border-b border-border/60 px-2",
          collapsed ? "justify-center" : "justify-between gap-2",
        )}
      >
        {collapsed ? (
          <Link
            to="/chat"
            aria-label="knode-os home"
            className="lemon-aura inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/90 text-primary-foreground shadow-sm transition hover:bg-primary"
          >
            <span className="font-serif text-base font-semibold leading-none">k</span>
          </Link>
        ) : (
          <Brand compact href="/chat" className="px-1" />
        )}
        {!collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={toggle}
                aria-label={t("nav.collapse")}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={6} className="text-xs">
              {t("nav.collapse")}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>

      {/* Primary nav — labeled in expanded mode, icon-only with
       * tooltips in collapsed mode. */}
      <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-2 py-2">
        {PRIMARY.map((item) => (
          <RailItem
            key={item.href}
            item={item}
            collapsed={collapsed}
            active={item.match ? item.match(pathname) : pathname === item.href}
          />
        ))}
      </nav>

      <div className="flex flex-col gap-0.5 border-t border-border/60 px-2 py-2">
        {SECONDARY.map((item) => (
          <RailItem
            key={item.href}
            item={item}
            collapsed={collapsed}
            active={item.match ? item.match(pathname) : pathname === item.href}
          />
        ))}
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={toggle}
                aria-label={t("nav.expand")}
                className="mx-auto mt-1 inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-card hover:text-foreground"
              >
                <PanelLeftOpen className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={6} className="text-xs">
              {t("nav.expand")}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
    </aside>
  );
}

function RailItem({
  item,
  active,
  collapsed,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
}) {
  const t = useT();
  const Icon = item.icon;
  const label = t(item.labelKey);

  // Collapsed = icon-only square with a Radix tooltip for the label.
  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={item.href}
            aria-label={label}
            aria-current={active ? "page" : undefined}
            className={cn(
              "group relative mx-auto inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors",
              active
                ? "bg-primary/15 text-primary"
                : "text-muted-foreground hover:bg-card hover:text-foreground",
            )}
          >
            {active ? (
              <span className="absolute -left-2 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-primary" />
            ) : null}
            <Icon className="h-[18px] w-[18px]" />
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={8} className="text-xs">
          {label}
        </TooltipContent>
      </Tooltip>
    );
  }

  // Expanded = label + icon row with the same active treatment.
  return (
    <Link
      to={item.href}
      aria-current={active ? "page" : undefined}
      className={cn(
        "group relative inline-flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium transition-colors",
        active
          ? "bg-primary/15 text-primary"
          : "text-muted-foreground hover:bg-card hover:text-foreground",
      )}
    >
      {active ? (
        <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-primary" />
      ) : null}
      <Icon className="h-[18px] w-[18px] shrink-0" />
      <span className="truncate">{label}</span>
    </Link>
  );
}
